import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import logo from "../../assets/logo.png";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const CHAT_AGENT_ID = "agent_3083bedaede5cd85dafe684014"; // latest version by default
const DEBUG_CHAT = (
  (typeof window !== "undefined" && window._RETELL_DEBUG === "true") ||
  process.env.REACT_APP_RETELL_DEBUG === "true"
);

const createMessageId = () => {
  const cryptoObj = typeof crypto !== "undefined" ? crypto : undefined;
  if (cryptoObj && cryptoObj.randomUUID) {
    return cryptoObj.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};

const getTypewriterConfig = (length) => {
  if (length > 1200) return { step: 4, delay: 10 };
  if (length > 600) return { step: 3, delay: 14 };
  if (length > 250) return { step: 2, delay: 18 };
  return { step: 1, delay: 24 };
};

const useTypewriter = (text, enabled) => {
  const [displayedText, setDisplayedText] = useState(enabled ? "" : text);
  const [isFinished, setIsFinished] = useState(!enabled);
  const { step, delay } = useMemo(() => getTypewriterConfig(text.length), [text.length]);

  useEffect(() => {
    if (!enabled) {
      setDisplayedText(text);
      setIsFinished(true);
      return undefined;
    }

    setDisplayedText("");
    setIsFinished(false);
    let index = 0;

    const timerId = setInterval(() => {
      index = Math.min(index + step, text.length);
      setDisplayedText(text.slice(0, index));
      if (index >= text.length) {
        clearInterval(timerId);
        setIsFinished(true);
      }
    }, delay);

    return () => clearInterval(timerId);
  }, [text, enabled, step, delay]);

  return { displayedText, isFinished };
};

const TypewriterContent = ({ content, animate, onComplete, onProgress }) => {
  const { displayedText, isFinished } = useTypewriter(content, animate);
  const hasNotifiedRef = useRef(false);

  useEffect(() => {
    if (!animate && !hasNotifiedRef.current) {
      hasNotifiedRef.current = true;
      if (onComplete) onComplete();
    }
    if (animate) {
      hasNotifiedRef.current = false;
    }
  }, [animate, onComplete]);

  useEffect(() => {
    if (animate && isFinished && !hasNotifiedRef.current) {
      hasNotifiedRef.current = true;
      if (onComplete) onComplete();
    }
  }, [animate, isFinished, onComplete]);

  useEffect(() => {
    if (animate && onProgress) {
      onProgress(isFinished);
    }
  }, [displayedText, animate, isFinished, onProgress]);

  if (animate && !isFinished) {
    return (
      <Typography
        component="div"
        sx={{
          whiteSpace: "pre-wrap",
          fontSize: "0.95rem",
          lineHeight: 1.55,
          fontFamily: "inherit",
          position: "relative",
          '@keyframes caretBlink': {
            '0%, 49%': { opacity: 1 },
            '50%, 100%': { opacity: 0 }
          },
          '&::after': {
            content: '"|"',
            ml: 0.2,
            display: 'inline-block',
            animation: 'caretBlink 1s steps(1, end) infinite',
            color: '#64748b'
          }
        }}
      >
        {displayedText}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        '@keyframes markdownFadeIn': {
          from: { opacity: 0.3 },
          to: { opacity: 1 }
        },
        animation: animate ? 'markdownFadeIn 220ms ease-in' : 'none'
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </Box>
  );
};

export const AgentAutopilotChatbot = () => {
  const [authorized, setAuthorized] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [authError, setAuthError] = useState("");

  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]); // {id, role: 'user'|'agent', content: string}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [completedAnimations, setCompletedAnimations] = useState(() => new Set());
  const messagesContainerRef = useRef(null);

  const scrollToBottom = useCallback((behavior = "auto") => {
    const node = messagesContainerRef.current;
    if (!node) return;

    if (typeof node.scrollTo === "function") {
      try {
        node.scrollTo({ top: node.scrollHeight, behavior });
        return;
      } catch (err) {
        // Fallback to direct assignment if smooth scrolling isn't supported
      }
    }

    node.scrollTop = node.scrollHeight;
  }, []);

  const proxyBase = useMemo(() => {
    // Prefer runtime override, then env, then default
    const envBase = process.env.REACT_APP_RETELL_PROXY_BASE;
    return window._RETELL_PROXY_BASE || envBase || "https://agent-autopilot-chatbot-proxy-717635562383.us-central1.run.app/api";
  }, []);

  const requiredPassword = useMemo(() => {
    const envPw = process.env.REACT_APP_RETELL_CHAT_PASSWORD;
    return window._RETELL_CHAT_PASSWORD || envPw || "retell-demo";
  }, []);

  useEffect(() => {
    scrollToBottom("auto");
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (DEBUG_CHAT) {
      // eslint-disable-next-line no-console
      console.info("[AgentAutopilotChatbot] proxyBase:", proxyBase);
    }
    if (proxyBase === "/api") {
      // eslint-disable-next-line no-console
      console.warn(
        "[AgentAutopilotChatbot] proxyBase is '/api'. On static hosting (e.g., Firebase without backend rewrites for /api/*), these requests will return index.html and break JSON parsing. Set window._RETELL_PROXY_BASE or REACT_APP_RETELL_PROXY_BASE to your backend base."
      );
    }
  }, [proxyBase]);

  const handleAuthorize = (e) => {
    e.preventDefault();
    if (passwordInput.trim() === requiredPassword) {
      setAuthorized(true);
      setAuthError("");
      return;
    }
    setAuthError("Incorrect password. Please try again.");
  };

  const appendMessage = (role, content) => {
    setMessages((prev) => [...prev, { id: createMessageId(), role, content }]);
  };

  const handleAnimationComplete = useCallback((id) => {
    setCompletedAnimations((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const handleTypewriterProgress = useCallback(() => {
    scrollToBottom("auto");
  }, [scrollToBottom]);

  const createChatIfNeeded = async () => {
    if (chatId) return chatId;
    try {
      const res = await fetch(`${proxyBase}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ agent_id: CHAT_AGENT_ID }),
      });
      if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        if (DEBUG_CHAT) {
          // eslint-disable-next-line no-console
          console.error("[chat:create] HTTP", res.status, errorText?.slice(0, 300));
        }
        throw new Error(`Failed to create chat (${res.status})`);
      }
      const contentType = res.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        const bodyText = await res.text().catch(() => "");
        if (DEBUG_CHAT) {
          // eslint-disable-next-line no-console
          console.error("[chat:create] Expected JSON, received:", bodyText?.slice(0, 300));
        }
        throw new Error("Non-JSON response while creating chat");
      }
      const data = await res.json();
      if (!data || !data.chat_id) {
        if (DEBUG_CHAT) {
          // eslint-disable-next-line no-console
          console.error("[chat:create] Missing chat_id in response", data);
        }
        throw new Error("Invalid response (missing chat_id)");
      }
      setChatId(data.chat_id);
      return data.chat_id;
    } catch (err) {
      if (DEBUG_CHAT) {
        // eslint-disable-next-line no-console
        console.error("[chat:create] Error", err);
      }
      throw err;
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    const userMsg = input.trim();
    setInput("");
    appendMessage("user", userMsg);

    try {
      const id = await createChatIfNeeded();
      const res = await fetch(`${proxyBase}/chat/${id}/completions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: userMsg }),
      });
      if (!res.ok) {
        const errorText = await res.text().catch(() => "");
        if (DEBUG_CHAT) {
          // eslint-disable-next-line no-console
          console.error("[chat:send] HTTP", res.status, errorText?.slice(0, 300));
        }
        throw new Error(`Failed to get completion (${res.status})`);
      }
      const contentType = res.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        const bodyText = await res.text().catch(() => "");
        if (DEBUG_CHAT) {
          // eslint-disable-next-line no-console
          console.error("[chat:send] Expected JSON, received:", bodyText?.slice(0, 300));
        }
        throw new Error("Non-JSON response from completion endpoint");
      }
      const data = await res.json();
      // Expecting { reply: string }
      if (data && typeof data.reply === "string") {
        appendMessage("agent", data.reply);
      } else if (data && data.message && typeof data.message.content === "string") {
        appendMessage("agent", data.message.content);
      } else if (data && typeof data.content === "string") {
        appendMessage("agent", data.content);
      } else {
        appendMessage("agent", "(No response content)");
      }
    } catch (err) {
      if (DEBUG_CHAT) {
        // eslint-disable-next-line no-console
        console.error("[chat:send] Error", err);
      }
      appendMessage("agent", DEBUG_CHAT ? `Error: ${err?.message || "Unknown error"}` : "There was an error processing your message.");
    } finally {
      setSending(false);
    }
  };

  const endChat = async () => {
    if (!chatId) return;
    try {
      await fetch(`${proxyBase}/chat/${chatId}/end`, { method: "POST" });
      appendMessage("agent", "Chat ended.");
    } catch (e) {
      if (DEBUG_CHAT) {
        // eslint-disable-next-line no-console
        console.error("[chat:end] Error", e);
      }
      // noop UI message already appended
    } finally {
      setChatId(null);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="full-page-background">
      <Grid item xs={12} md={7}>
        <Container className="App" maxWidth="sm">
          <img src={logo} className="App-logo" alt="logo" />
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <h1 style={{
              color: '#FFFFFF',
              fontSize: '1.8rem',
              fontWeight: 'bold',
              margin: '1rem',
            }}>
              Customer Support Chatbot DEMO
            </h1>
            <div style={{
              height: '4px',
              width: '100px',
              background: 'linear-gradient(90deg, #a0b8ff, #8a2be2)',
              margin: '0 auto',
              borderRadius: '2px'
            }}></div>
          </div>

          {!authorized ? (
            <form onSubmit={handleAuthorize}>
              <Stack spacing={2}>
                <Typography sx={{ color: '#d0d0d0', textAlign: 'center' }}>
                  Enter the demo password to access the chatbot
                </Typography>
                <TextField
                  type="password"
                  label="Password"
                  variant="filled"
                  sx={{ backgroundColor: 'white', borderRadius: '4px' }}
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  required
                />
                {authError && (
                  <Typography sx={{ color: '#ffb4b4' }}>{authError}</Typography>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    background: 'linear-gradient(45deg, #1976d2 30%, #2196f3 90%)',
                    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                    color: 'white',
                    height: '56px',
                    padding: '0 30px',
                  }}
                >
                  Unlock Chatbot
                </Button>
              </Stack>
            </form>
          ) : (
            <Box sx={{ p: '1px', borderRadius: '16px', background: 'linear-gradient(135deg, rgba(100,181,246,0.6), rgba(138,43,226,0.6))', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: { xs: 460, sm: 520 }, background: 'rgba(255,255,255,0.96)', borderRadius: '16px' }}>
                <Box
                  ref={messagesContainerRef}
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: 2,
                    background: 'linear-gradient(180deg, rgba(245,247,250,0.9), rgba(255,255,255,0.95))',
                    borderTopLeftRadius: '16px',
                    borderTopRightRadius: '16px',
                    textAlign: 'left'
                  }}
                >
                  {messages.length === 0 && (
                    <Typography sx={{ color: '#475569' }}>
                      Say hello to start a new chat with the agent.
                    </Typography>
                  )}
                  <Stack spacing={1.2}>
                    {messages.map((m, idx) => {
                      const messageId = m.id ?? `${m.role}-${idx}`;
                      const isAgent = m.role === 'agent';
                      const shouldAnimate = Boolean(m.id) && isAgent && !completedAnimations.has(m.id);

                      return (
                        <Box
                          key={messageId}
                          sx={{
                            alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                            maxWidth: '85%',
                            background: isAgent ? '#eef2f6' : 'linear-gradient(135deg, #d0e8ff, #b7d7ff)',
                            color: '#111',
                            borderRadius: isAgent ? '14px 14px 14px 4px' : '14px 14px 4px 14px',
                            padding: '10px 12px',
                            boxShadow: '0 3px 10px rgba(0,0,0,0.08)'
                          }}
                        >
                          <Box
                            sx={{
                              textAlign: 'left',
                              fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
                              '& p': { m: 0, mb: 0.75, fontSize: '0.95rem', lineHeight: 1.55 },
                              '& ul, & ol': { m: 0, my: 0.75, pl: 2.5, listStylePosition: 'outside' },
                              '& li': { mb: 0.4, fontSize: '0.95rem', lineHeight: 1.55 },
                              '& li p': { fontSize: '0.95rem', lineHeight: 1.55 },
                              '& li > p': { m: 0 },
                              '& li + li': { mt: 0.25 },
                              '& li > ul, & li > ol': { my: 0.5 },
                              '& strong': { fontWeight: 700 },
                              '& a': { color: '#1976d2', textDecoration: 'underline' },
                              '& code': { backgroundColor: '#f1f5f9', px: 0.5, borderRadius: '4px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
                              '& pre': { backgroundColor: '#0b1b2b', color: '#fff', p: 1, borderRadius: '8px', overflowX: 'auto', mt: 0.75 },
                              '& h1, & h2, & h3, & h4, & h5, & h6': { m: 0, mt: 1.25, mb: 0.5, fontWeight: 700, lineHeight: 1.25 },
                              '& h1:first-of-type, & h2:first-of-type, & h3:first-of-type, & h4:first-of-type, & h5:first-of-type, & h6:first-of-type': { mt: 0.25 },
                              '& blockquote': { m: 0, my: 0.75, pl: 2, borderLeft: '3px solid #cbd5e1', color: '#334155', fontStyle: 'italic' },
                              '& hr': { my: 1, border: 0, borderTop: '1px solid #e2e8f0' }
                            }}
                          >
                            <TypewriterContent
                              content={m.content}
                              animate={shouldAnimate}
                              onComplete={shouldAnimate ? () => handleAnimationComplete(m.id) : undefined}
                              onProgress={shouldAnimate ? handleTypewriterProgress : undefined}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>

                <Box sx={{ p: 1.5, borderTop: '1px solid #eef2f7', background: 'rgba(255,255,255,0.96)', borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px', position: 'sticky', bottom: 0 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      placeholder="Type your message..."
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={onKeyDown}
                      variant="filled"
                      size="small"
                      InputProps={{ disableUnderline: true }}
                      sx={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        '& .MuiInputBase-root': { height: 44 },
                        '& .MuiInputBase-input': { paddingTop: 0, paddingBottom: 0 }
                      }}
                      fullWidth
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={sending || !input.trim()}
                      variant="contained"
                      sx={{
                        background: 'linear-gradient(45deg, #1976d2 30%, #2196f3 90%)',
                        color: 'white',
                        paddingX: '18px',
                        minWidth: { xs: '100%', sm: '96px' },
                        height: 44
                      }}
                    >
                      Send
                    </Button>
                    <Button
                      onClick={endChat}
                      disabled={!chatId}
                      variant="outlined"
                      sx={{
                        color: '#0b1b2b',
                        borderColor: '#cfd8e3',
                        background: 'white',
                        minWidth: { xs: '100%', sm: '110px' },
                        height: 44
                      }}
                    >
                      End Chat
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Box>
          )}
        </Container>
      </Grid>
    </div>
  );
};

export default AgentAutopilotChatbot;


